import type { StateCreator } from 'zustand'
import { storeLogger } from '@/utils/logger'
import type {
  BuilderDesignConfig,
  BuilderDesignConfigPatch,
  BuilderRulesConfig,
  ChatConfiguration,
  ConfigUpdateType,
  ConfigurationState,
  ProductConfig,
  UtmParameter,
} from './types'

export interface ConfigurationSlice extends ConfigurationState {
  // Local configuration management (editing state only)
  loadConfigurationLocal: (configuration: ChatConfiguration) => void
  updateConfigurationLocal: (
    data: Partial<ChatConfiguration>,
    section?: string
  ) => void

  // Section-specific local updates (no API calls)
  updateRulesConfigLocal: (config: Partial<BuilderRulesConfig>) => void
  updateProductConfigLocal: (config: Partial<ProductConfig>) => void
  updateDesignConfigLocal: (config: BuilderDesignConfigPatch) => void
  updateUtmParamsLocal: (params: UtmParameter[]) => void

  // Asset reference updates
  updateAssetReference: (updates: {
    aiMessengerIconAssetId?: string | null | undefined
    sendButtonIconAssetId?: string | null | undefined
    launcherAvatarAssetId?: string | null | undefined
  }) => Promise<void>
  setAiMessengerIcon: (assetId: string | null) => Promise<void>
  setSendButtonIcon: (assetId: string | null) => Promise<void>

  // Utility actions
  markAsChanged: () => void
  clearChanges: () => void
  clearError: () => void
  setLoading: (loading: boolean) => void
  checkUnpublishedChanges: () => void
  clearUnpublishedChanges: () => void

  // Update type tracking
  setPendingUpdateTypes: (types: ConfigUpdateType[]) => void
  clearPendingUpdateTypes: () => void
  addPendingUpdateType: (type: ConfigUpdateType) => void
}

export const createConfigurationSlice: StateCreator<
  ConfigurationSlice,
  [],
  [],
  ConfigurationSlice
> = (set, get) => ({
  // Initial state
  currentConfiguration: null,
  isLoading: false,
  error: null,
  hasUnsavedChanges: false,
  hasUnpublishedChanges: false,
  pendingUpdateTypes: [],

  // Local configuration management (no API calls)
  loadConfigurationLocal: (configuration: ChatConfiguration) => {
    set({
      currentConfiguration: {
        ...configuration,
        assets: configuration.assets || [],
      },
      hasUnsavedChanges: false,
      isLoading: false,
      error: null,
    })

    // Check for unpublished changes after loading
    get().checkUnpublishedChanges()
  },

  // Immediate local update for preview + mark as changed
  updateConfigurationLocal: (
    data: Partial<ChatConfiguration>,
    section?: string
  ) => {
    const state = get()
    const { currentConfiguration, addPendingUpdateType } = state

    if (!currentConfiguration) return

    const updatedConfig = {
      ...currentConfiguration,
      ...data,
      updatedAt: new Date().toISOString(),
    }

    storeLogger.debug('Configuration updated locally', {
      id: updatedConfig.id,
      section,
      hasChanges: true,
    })

    set({
      currentConfiguration: updatedConfig,
      hasUnsavedChanges: true,
    })

    // Track what type of update was made
    if (section) {
      addPendingUpdateType(section as ConfigUpdateType)
    }

    get().checkUnpublishedChanges()
  },

  // Section-specific local updates
  updateRulesConfigLocal: (config: Partial<BuilderRulesConfig>) => {
    const state = get()
    const currentRulesConfig = state.currentConfiguration?.rulesConfig

    // No configuration loaded yet — updateConfigurationLocal would no-op anyway.
    if (!currentRulesConfig) return

    // Deep merge the partial config with existing rules config
    const mergedRulesConfig: BuilderRulesConfig = { ...currentRulesConfig }

    Object.keys(config).forEach((key) => {
      const typedKey = key as keyof BuilderRulesConfig
      const newValue = config[typedKey]
      const currentValue = currentRulesConfig[typedKey]

      if (
        newValue &&
        typeof newValue === 'object' &&
        !Array.isArray(newValue) &&
        currentValue &&
        typeof currentValue === 'object' &&
        !Array.isArray(currentValue)
      ) {
        // Deep merge nested objects
        mergedRulesConfig[typedKey] = { ...currentValue, ...newValue } as any
      } else {
        // Replace primitive values or arrays
        mergedRulesConfig[typedKey] = newValue as any
      }
    })

    get().updateConfigurationLocal({ rulesConfig: mergedRulesConfig }, 'rules')
  },

  updateProductConfigLocal: (config: Partial<ProductConfig>) => {
    const state = get()
    const currentProductConfig = state.currentConfiguration?.productConfig

    // No configuration loaded yet — updateConfigurationLocal would no-op anyway.
    if (!currentProductConfig) return

    // Deep merge the partial config with existing product config
    const mergedProductConfig: ProductConfig = { ...currentProductConfig }

    Object.keys(config).forEach((key) => {
      const typedKey = key as keyof ProductConfig
      const newValue = config[typedKey]
      const currentValue = currentProductConfig[typedKey]

      if (
        newValue &&
        typeof newValue === 'object' &&
        !Array.isArray(newValue) &&
        currentValue &&
        typeof currentValue === 'object' &&
        !Array.isArray(currentValue)
      ) {
        // Deep merge nested objects
        mergedProductConfig[typedKey] = { ...currentValue, ...newValue } as any
      } else {
        // Replace primitive values or arrays
        mergedProductConfig[typedKey] = newValue as any
      }
    })

    get().updateConfigurationLocal(
      { productConfig: mergedProductConfig },
      'productCatalog'
    )
  },

  updateDesignConfigLocal: (config: BuilderDesignConfigPatch) => {
    const state = get()
    const currentDesignConfig = state.currentConfiguration?.designConfig

    // No configuration loaded yet — updateConfigurationLocal would no-op anyway.
    if (!currentDesignConfig) return

    // Deep merge the partial config with existing design config
    const mergedDesignConfig: BuilderDesignConfig = { ...currentDesignConfig }

    // Merge top-level properties, handling nested objects specially
    Object.keys(config).forEach((key) => {
      const typedKey = key as keyof BuilderDesignConfig
      const newValue = config[typedKey]
      const currentValue = currentDesignConfig[typedKey]

      if (
        newValue &&
        typeof newValue === 'object' &&
        !Array.isArray(newValue) &&
        currentValue &&
        typeof currentValue === 'object' &&
        !Array.isArray(currentValue)
      ) {
        // Deep merge nested objects
        mergedDesignConfig[typedKey] = { ...currentValue, ...newValue } as any
      } else {
        // Replace primitive values or arrays
        mergedDesignConfig[typedKey] = newValue as any
      }
    })

    get().updateConfigurationLocal(
      { designConfig: mergedDesignConfig },
      'design'
    )
  },

  updateUtmParamsLocal: (params: UtmParameter[]) => {
    get().updateConfigurationLocal({ utmParams: params })
  },

  // Asset reference updates (these still make API calls as they're asset-specific)
  updateAssetReference: async (updates: {
    aiMessengerIconAssetId?: string | null | undefined
    sendButtonIconAssetId?: string | null | undefined
    launcherAvatarAssetId?: string | null | undefined
  }) => {
    const { currentConfiguration } = get()
    if (!currentConfiguration) return

    // Update local state immediately
    get().updateConfigurationLocal(updates)
  },

  setAiMessengerIcon: async (assetId: string | null) => {
    get().updateAssetReference({ aiMessengerIconAssetId: assetId })
  },

  setSendButtonIcon: async (assetId: string | null) => {
    get().updateAssetReference({ sendButtonIconAssetId: assetId })
  },

  // Utility actions
  markAsChanged: () => {
    set({ hasUnsavedChanges: true })
  },

  clearChanges: () => {
    set({
      hasUnsavedChanges: false,
      pendingUpdateTypes: [],
    })
  },

  clearError: () => {
    set({ error: null })
  },

  setLoading: (loading: boolean) => {
    set({ isLoading: loading })
  },

  checkUnpublishedChanges: () => {
    const { currentConfiguration } = get()

    if (!currentConfiguration) {
      set({ hasUnpublishedChanges: false })
      return
    }

    // Check if there are unpublished changes by comparing published vs current state
    // For now, we'll check if there's a published version and any changes
    const hasUnpublishedChanges =
      !currentConfiguration.publishedAt || get().hasUnsavedChanges

    set({ hasUnpublishedChanges })
  },

  clearUnpublishedChanges: () => {
    set({ hasUnpublishedChanges: false })
  },

  // Update type tracking
  setPendingUpdateTypes: (types: ConfigUpdateType[]) => {
    set({ pendingUpdateTypes: types })
  },

  clearPendingUpdateTypes: () => {
    set({ pendingUpdateTypes: [] })
  },

  addPendingUpdateType: (type: ConfigUpdateType) => {
    const { pendingUpdateTypes } = get()
    if (!pendingUpdateTypes.includes(type)) {
      set({
        pendingUpdateTypes: [...pendingUpdateTypes, type],
      })
    }
  },
})
